/**
 * KAN-783: чистые функции редактора атрибутов лида ("Что бот выясняет у
 * клиента"): новый атрибут, разбор вариантов через запятую, проверка перед
 * сохранением (ошибки по id строки), подготовка к отправке, перестановка строк.
 */
import type { LeadAttribute, LeadAttributeType } from '@/types/settings';

export const LEAD_ATTRIBUTE_TYPES: readonly { value: LeadAttributeType; label: string }[] = [
  { value: 'text', label: 'Текст' },
  { value: 'bool', label: 'Да / нет' },
  { value: 'choice', label: 'Выбор из вариантов' },
];

/** Ошибки проверки: id атрибута -> текст под полем названия. */
export type LeadAttributeErrors = Record<string, string>;

export const RENAME_WARNING =
  'Название атрибута это ключ значения в карточке: после переименования старые значения не видны.';

function shortId(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return uuid.replace(/-/g, '').slice(0, 8);
  return Math.random().toString(16).slice(2, 10).padEnd(8, '0');
}

export function newAttribute(): LeadAttribute {
  return { id: shortId(), name: '', hint: '', type: 'text', options: [], show_in_crm: true, attention: false, reply_rule: '' };
}

/** "звонок, чат ,, звонок" -> ["звонок", "чат"]. */
export function parseOptions(raw: string): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const part of raw.split(',')) {
    const value = part.trim();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    result.push(value);
  }
  return result;
}

/**
 * Названия, занятые встроенными полями карточки (ключи lead_info на сервере);
 * backend может отдать свой список в lead_attribute_reserved_names.
 */
export const RESERVED_NAMES: readonly string[] = ['ЛПР', 'ЛПР роль', 'ЛПР телефон', 'ЛПР Telegram', 'ЛПР email', 'Компания'];

/** Ключ названия как на сервере: без крайних и двойных пробелов, без учёта регистра. */
export function nameKey(name: string): string {
  return name.trim().split(/\s+/).join(' ').toLowerCase();
}

/**
 * Пустое, зарезервированное или повторяющееся название, выбор без вариантов.
 * Пусто: можно сохранять. `reservedNames` - список backend, иначе RESERVED_NAMES.
 */
export function validateAttributes(
  attributes: readonly LeadAttribute[],
  reservedNames: readonly string[] = RESERVED_NAMES
): LeadAttributeErrors {
  const errors: LeadAttributeErrors = {};
  const reserved = new Set(reservedNames.map(nameKey));
  const seen = new Set<string>();
  for (const attribute of attributes) {
    const key = nameKey(attribute.name);
    if (!key) errors[attribute.id] = 'Введите название';
    else if (reserved.has(key)) errors[attribute.id] = 'Название зарезервировано';
    else if (seen.has(key)) errors[attribute.id] = 'Название повторяется';
    else if (attribute.type === 'choice' && attribute.options.length === 0) errors[attribute.id] = 'Укажите варианты через запятую';
    seen.add(key);
  }
  return errors;
}

/** Список к отправке: тексты без крайних пробелов, варианты только у choice. */
export function prepareAttributes(attributes: readonly LeadAttribute[]): LeadAttribute[] {
  return attributes.map((attribute) => ({
    ...attribute,
    name: attribute.name.trim(),
    hint: attribute.hint.trim(),
    reply_rule: attribute.reply_rule.trim(),
    options: attribute.type === 'choice' ? attribute.options : [],
  }));
}

/** Строка `index` сдвинута на `delta` (-1 вверх, 1 вниз); за границей: тот же массив. */
export function moveAttribute(attributes: readonly LeadAttribute[], index: number, delta: -1 | 1): LeadAttribute[] {
  const target = index + delta;
  if (target < 0 || target >= attributes.length) return attributes as LeadAttribute[];
  const next = [...attributes];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}
